import { FC, useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
  Button,
  useToast,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import TextInput from "./TextInput";
import { useAuthContext } from "./AuthProvider";
import { profileValidationSchema } from "utils/validationSchema";
import { useProfileModal } from "states/profileModal";
import { updateEmail, updateProfile } from "firebase/auth";
import { auth, db } from "connections/firebase";
import { getErrorMessage } from "utils";
import { doc, updateDoc } from "firebase/firestore";

interface IProfileFormPayload {
  name: string;
  email: string;
}

const Profile: FC = () => {
  const [initialValues, setInitialValues] = useState<IProfileFormPayload>({
    name: "",
    email: "",
  });
  const [isLoading, setLoading] = useState(false);
  const { user, setUser } = useAuthContext();
  const { isOpen, onClose } = useProfileModal();
  const toast = useToast();

  useEffect(() => {
    if (user) {
      setInitialValues({
        name: user.name,
        email: user.email,
      });
    }
  }, [user]);

  return (
    <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Profile</ModalHeader>
        <ModalCloseButton />
        <Formik<IProfileFormPayload>
          onSubmit={async (values) => {
            try {
              if (auth.currentUser && user) {
                setLoading(true);
                if (values.email !== initialValues.email) {
                  await updateEmail(auth.currentUser, values.email);
                }

                await updateProfile(auth.currentUser, {
                  displayName: values.name,
                });

                await updateDoc(doc(db, "admins", auth.currentUser.uid), {
                  email: values.email,
                  name: values.name,
                });

                const updatedUser = {
                  ...user,
                  name: values.name,
                  email: values.email,
                };

                setUser(updatedUser);
                toast({
                  title: "Success",
                  description: "Update profile berhasil!",
                  status: "success",
                  isClosable: true,
                });
                onClose();
              }
            } catch (error) {
              toast({
                title: "Error",
                description: getErrorMessage(error),
                status: "error",
                isClosable: true,
              });
            } finally {
              setLoading(false);
            }
          }}
          initialValues={initialValues}
          enableReinitialize
          validationSchema={profileValidationSchema}
        >
          <Form>
            <ModalBody>
              <Stack spacing={2}>
                <TextInput name="name" id="name" label="Nama" />
                <TextInput name="email" id="email" label="Email" />
              </Stack>
            </ModalBody>
            <ModalFooter>
              <Button type="reset" colorScheme="red" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button isLoading={isLoading} type="submit" colorScheme="blue">
                Submit
              </Button>
            </ModalFooter>
          </Form>
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default Profile;
